import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const dataBadges = [
  { icon: "ri-bar-chart-2-line", value: "$4.5M+ USD", label: "Annual Domestic Distribution" },
  { icon: "ri-database-2-line", value: "15,000+ SKUs", label: "Verified by Installation Data" },
  { icon: "ri-car-line", value: "2004–2024", label: "Euro-Spec Vehicle Coverage" },
  { icon: "ri-global-line", value: "15+ Markets", label: "Active Export Destinations" },
];

const slides = [
  {
    src: "https://public.readdy.ai/ai/img_res/e3f2bca59b604cc6e5a897e17da80602.jpg",
    alt: "European engine components robotic assembly line automotive supply chain",
  },
  {
    src: "https://readdy.ai/api/search-image?query=dramatic%20close%20up%20cinematic%20photograph%20of%20glowing%20red%20hot%20European%20V8%20engine%20block%20being%20precision%20machined%20on%20CNC%20equipment%20in%20a%20dark%20industrial%20factory%20with%20dramatic%20lighting%20showing%20pistons%20crankshaft%20and%20precision%20engineering%20ultra%20high%20resolution&width=1600&height=900&seq=hero-slide-engine-v53&orientation=landscape",
    alt: "European engine block precision machining OE parts wholesale supply",
  },
  {
    src: "https://readdy.ai/api/search-image?query=cinematic%20aerial%20photograph%20of%20massive%20container%20ship%20loaded%20with%20shipping%20containers%20at%20sea%20at%20golden%20hour%20dramatic%20sky%20ocean%20freight%20logistics%20global%20supply%20chain%20ultra%20high%20resolution%20commercial%20photography&width=1600&height=900&seq=hero-slide-ship-v53&orientation=landscape",
    alt: "FCL LCL container ship Guangzhou Port Ningbo Port global freight logistics",
  },
  {
    src: "https://readdy.ai/api/search-image?query=cinematic%20night%20photograph%20of%20massive%20freight%20train%20cargo%20locomotive%20speeding%20through%20dark%20landscape%20with%20motion%20blur%20light%20trails%20China%20Europe%20railway%20express%20logistics%20ultra%20high%20resolution%20dramatic%20photography&width=1600&height=900&seq=hero-slide-train-v53&orientation=landscape",
    alt: "China Europe Railway Express freight train logistics supply chain",
  },
];

const SILVER = "url(#silver-grad)";

// Shared silver gradient def — used by all logos
const SilverDefs = () => (
  <defs>
    <linearGradient id="silver-grad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stopColor="#d0d0d0" />
      <stop offset="40%" stopColor="#ffffff" />
      <stop offset="100%" stopColor="#a8a8a8" />
    </linearGradient>
  </defs>
);

const brandList = [
  {
    name: "Mercedes-Benz",
    label: "MERCEDES-BENZ",
    href: "/catalog?brand=mercedes",
    // Three-pointed star inside circle — exact geometry
    logo: (
      <svg viewBox="0 0 100 100" width="46" height="46" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        {/* Outer ring */}
        <circle cx="50" cy="50" r="45" fill="none" stroke={SILVER} strokeWidth="5" />
        {/* Inner ring */}
        <circle cx="50" cy="50" r="36" fill="none" stroke={SILVER} strokeWidth="1.5" />
        {/* Three-pointed star: top point, bottom-right, bottom-left */}
        <polygon
          points="50,14 62.12,68.5 37.88,68.5"
          fill="none" stroke={SILVER} strokeWidth="3.5"
          strokeLinejoin="round"
        />
        {/* Star center fill */}
        <circle cx="50" cy="50" r="4" fill={SILVER} />
      </svg>
    ),
  },
  {
    name: "BMW",
    label: "BMW",
    href: "/catalog?brand=bmw",
    // BMW roundel: outer ring + inner ring + 4 quadrants (2 filled)
    logo: (
      <svg viewBox="0 0 100 100" width="46" height="46" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        <circle cx="50" cy="50" r="45" fill="none" stroke={SILVER} strokeWidth="5" />
        <circle cx="50" cy="50" r="32" fill="none" stroke={SILVER} strokeWidth="2" />
        {/* Vertical divider */}
        <line x1="50" y1="18" x2="50" y2="82" stroke={SILVER} strokeWidth="2" />
        {/* Horizontal divider */}
        <line x1="18" y1="50" x2="82" y2="50" stroke={SILVER} strokeWidth="2" />
        {/* Top-left quadrant filled (white/silver) */}
        <path d="M50 18 A32 32 0 0 0 18 50 L50 50 Z" fill={SILVER} opacity="0.85" />
        {/* Bottom-right quadrant filled */}
        <path d="M50 82 A32 32 0 0 0 82 50 L50 50 Z" fill={SILVER} opacity="0.85" />
      </svg>
    ),
  },
  {
    name: "Audi",
    label: "AUDI",
    href: "/catalog?brand=audi",
    // Four interlocking rings — precise overlap
    logo: (
      <svg viewBox="0 0 172 60" width="86" height="30" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        <circle cx="22" cy="30" r="19" fill="none" stroke={SILVER} strokeWidth="4.5" />
        <circle cx="58" cy="30" r="19" fill="none" stroke={SILVER} strokeWidth="4.5" />
        <circle cx="94" cy="30" r="19" fill="none" stroke={SILVER} strokeWidth="4.5" />
        <circle cx="130" cy="30" r="19" fill="none" stroke={SILVER} strokeWidth="4.5" />
      </svg>
    ),
  },
  {
    name: "Volkswagen",
    label: "VOLKSWAGEN",
    href: "/catalog?brand=volkswagen",
    // VW logo: circle + V over W letterform
    logo: (
      <svg viewBox="0 0 100 100" width="46" height="46" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        <circle cx="50" cy="50" r="45" fill="none" stroke={SILVER} strokeWidth="5" />
        {/* V shape — upper half */}
        <polyline
          points="35,22 50,52 65,22"
          fill="none" stroke={SILVER} strokeWidth="5.5"
          strokeLinecap="round" strokeLinejoin="round"
        />
        {/* W shape — lower half */}
        <polyline
          points="24,44 35,70 50,52 65,70 76,44"
          fill="none" stroke={SILVER} strokeWidth="5.5"
          strokeLinecap="round" strokeLinejoin="round"
        />
        {/* Horizontal separator line */}
        <line x1="28" y1="48" x2="72" y2="48" stroke={SILVER} strokeWidth="2" />
      </svg>
    ),
  },
  {
    name: "Porsche",
    label: "PORSCHE",
    href: "/catalog?brand=porsche",
    // Porsche crest: shield outline + horse silhouette + antlers
    logo: (
      <svg viewBox="0 0 80 100" width="38" height="46" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        {/* Shield outline */}
        <path
          d="M40 4 L76 18 L76 58 Q76 82 40 96 Q4 82 4 58 L4 18 Z"
          fill="none" stroke={SILVER} strokeWidth="3.5" strokeLinejoin="round"
        />
        {/* Center vertical divider */}
        <line x1="40" y1="4" x2="40" y2="96" stroke={SILVER} strokeWidth="2" />
        {/* Horizontal divider */}
        <line x1="4" y1="50" x2="76" y2="50" stroke={SILVER} strokeWidth="2" />
        {/* Horse body (simplified silhouette) */}
        <path
          d="M40 28 Q44 22 50 24 Q54 26 52 32 Q56 30 58 34 Q54 36 52 40 Q50 46 44 48 L36 48 Q30 46 28 40 Q26 36 22 34 Q24 30 28 32 Q26 26 30 24 Q36 22 40 28 Z"
          fill={SILVER} opacity="0.9"
        />
        {/* Antlers left */}
        <path d="M30 24 L26 14 M28 20 L22 16" stroke={SILVER} strokeWidth="2.5" strokeLinecap="round" fill="none" />
        {/* Antlers right */}
        <path d="M50 24 L54 14 M52 20 L58 16" stroke={SILVER} strokeWidth="2.5" strokeLinecap="round" fill="none" />
        {/* Legs */}
        <path d="M34 48 L32 62 M38 48 L37 62 M42 48 L43 62 M46 48 L48 62" stroke={SILVER} strokeWidth="2.5" strokeLinecap="round" fill="none" />
        {/* Stripe pattern top-left & bottom-right */}
        <path d="M4 18 L4 50 L40 50 L40 4 Z" fill={SILVER} opacity="0.08" />
        <path d="M40 50 L40 96 Q76 82 76 58 L76 50 Z" fill={SILVER} opacity="0.08" />
      </svg>
    ),
  },
  {
    name: "Jaguar",
    label: "JAGUAR",
    href: "/catalog?brand=jaguar",
    // Jaguar leaping cat — clean silhouette
    logo: (
      <svg viewBox="0 0 110 60" width="66" height="36" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        {/* Body arc */}
        <path
          d="M8 42 C12 20 28 10 48 14 C58 8 72 6 84 14 C96 10 106 18 104 32 C98 26 88 24 80 28 C70 16 54 16 44 26 C32 18 16 26 8 42 Z"
          fill={SILVER} opacity="0.92"
        />
        {/* Head */}
        <ellipse cx="96" cy="28" rx="10" ry="8" fill={SILVER} opacity="0.92" />
        {/* Ear */}
        <path d="M100 22 L106 14 L108 22 Z" fill={SILVER} opacity="0.92" />
        {/* Front legs */}
        <path d="M44 26 L38 44 L42 44 L46 30" fill={SILVER} opacity="0.92" />
        <path d="M52 24 L50 44 L54 44 L56 28" fill={SILVER} opacity="0.92" />
        {/* Rear legs */}
        <path d="M20 36 L16 50 L20 50 L24 40" fill={SILVER} opacity="0.92" />
        <path d="M30 30 L28 50 L32 50 L34 36" fill={SILVER} opacity="0.92" />
        {/* Tail */}
        <path d="M8 42 C4 38 2 30 6 24 C8 28 8 36 8 42 Z" fill={SILVER} opacity="0.92" />
      </svg>
    ),
  },
  {
    name: "Land Rover",
    label: "LAND ROVER",
    href: "/catalog?brand=landrover",
    // Land Rover oval badge with wordmark
    logo: (
      <svg viewBox="0 0 150 60" width="80" height="32" xmlns="http://www.w3.org/2000/svg">
        <SilverDefs />
        {/* Outer oval */}
        <ellipse cx="75" cy="30" rx="72" ry="26" fill="none" stroke={SILVER} strokeWidth="3.5" />
        {/* Inner oval */}
        <ellipse cx="75" cy="30" rx="64" ry="19" fill="none" stroke={SILVER} strokeWidth="1.5" />
        {/* Wordmark */}
        <text
          x="75" y="35"
          textAnchor="middle"
          fontFamily="'Arial Narrow', Arial, sans-serif"
          fontWeight="700"
          fontSize="14"
          letterSpacing="3.5"
          fill={SILVER}
        >
          LAND ROVER
        </text>
      </svg>
    ),
  },
];

function BrandLogoRow() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const onMouseUp = useCallback(() => setIsDragging(false), []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (!scrollRef.current) return;
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!scrollRef.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  }, [startX, scrollLeft]);

  return (
    <div className="w-full max-w-4xl">
      <p
        className="text-center tracking-widest uppercase mb-6 text-white/35"
        style={{ letterSpacing: "0.22em", fontSize: "9px" }}
      >
        OEM Coverage — European Brands
      </p>

      {/* Desktop: flex row; Mobile: horizontal scroll carousel */}
      <div
        ref={scrollRef}
        className="flex items-center gap-4 md:gap-1 md:justify-between overflow-x-auto md:overflow-x-visible select-none"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          cursor: isDragging ? "grabbing" : "grab",
          WebkitOverflowScrolling: "touch",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
      >
        {brandList.map((brand) => (
          <a
            key={brand.name}
            href={brand.href}
            title={brand.name}
            className="flex flex-col items-center gap-2 cursor-pointer group transition-all duration-300 hover:scale-110 shrink-0"
            style={{ textDecoration: "none", minWidth: "72px" }}
            draggable={false}
          >
            <div
              className="flex items-center justify-center transition-all duration-300 group-hover:drop-shadow-[0_0_14px_rgba(255,255,255,0.6)]"
              style={{ filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.5))" }}
            >
              {brand.logo}
            </div>
            <span
              className="whitespace-nowrap font-semibold tracking-widest transition-colors duration-300 group-hover:text-amber-300"
              style={{
                fontSize: "7px",
                letterSpacing: "0.12em",
                color: "rgba(255,255,255,0.55)",
              }}
            >
              {brand.label}
            </span>
          </a>
        ))}
      </div>

      {/* Mobile swipe hint */}
      <p className="md:hidden text-center mt-3 text-white/25" style={{ fontSize: "9px", letterSpacing: "0.15em" }}>
        ← SWIPE TO SEE ALL BRANDS →
      </p>
    </div>
  );
}

export default function HeroSection() {
  const [query, setQuery] = useState("");
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const handleSearch = () => {
    const q = query.trim();
    if (q) {
      navigate(`/catalog?search=${encodeURIComponent(q)}`);
    } else {
      navigate("/catalog");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };
  const [next, setNext] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = (idx: number) => {
    if (transitioning || idx === current) return;
    setNext(idx);
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(idx);
      setNext(null);
      setTransitioning(false);
    }, 900);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrent((c) => {
        const n = (c + 1) % slides.length;
        setNext(n);
        setTransitioning(true);
        setTimeout(() => {
          setCurrent(n);
          setNext(null);
          setTransitioning(false);
        }, 900);
        return c;
      });
    }, 4500);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* ── Cinematic Montage Slider ── */}
      <div className="absolute inset-0">
        <img
          key={`cur-${current}`}
          src={slides[current].src}
          alt={slides[current].alt}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: transitioning ? 0 : 1, transition: "opacity 0.9s ease-in-out" }}
        />
        {next !== null && (
          <img
            key={`nxt-${next}`}
            src={slides[next].src}
            alt={slides[next].alt}
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ opacity: transitioning ? 1 : 0, transition: "opacity 0.9s ease-in-out" }}
          />
        )}
        {/* Lighter warm overlay — daytime industrial feel */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to bottom, rgba(15,20,30,0.45) 0%, rgba(15,20,30,0.25) 40%, rgba(15,20,30,0.72) 100%)",
          }}
        />
        {/* Warm amber vignette at bottom */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at bottom center, rgba(245,158,11,0.08) 0%, transparent 65%)",
          }}
        />
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="cursor-pointer transition-all duration-300"
            style={{
              width: i === current ? "24px" : "6px",
              height: "6px",
              borderRadius: "3px",
              background: i === current ? "#F59E0B" : "rgba(255,255,255,0.35)",
            }}
          />
        ))}
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-6 flex flex-col items-center text-center pt-24 md:pt-28 pb-16 md:pb-20">
        {/* Eyebrow */}
        <div
          className="flex items-center gap-2 md:gap-3 mb-7 md:mb-10 px-4 md:px-5 py-2 rounded-full"
          style={{
            background: "rgba(245,158,11,0.12)",
            border: "1px solid rgba(245,158,11,0.35)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: "#F59E0B" }} />
          <p className="text-xs tracking-widest uppercase font-medium" style={{ color: "#FCD34D", letterSpacing: "0.15em", fontSize: "clamp(8px, 2vw, 11px)" }}>
            Global B2B European Engine Components &amp; Supply Chain Platform
          </p>
        </div>

        {/* H1 */}
        <h1
          className="font-thin leading-none mb-6 md:mb-8 text-white w-full"
          style={{ fontSize: "clamp(1.9rem, 6vw, 4.8rem)", letterSpacing: "0.03em", textShadow: "0 2px 40px rgba(0,0,0,0.3)" }}
        >
          DATA-DRIVEN AUTO PARTS
          <br />
          <span
            style={{
              background: "linear-gradient(90deg, #F59E0B 0%, #FCD34D 50%, #F59E0B 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            SUPPLY CHAIN.
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-sm md:text-base font-light leading-relaxed mb-8 md:mb-10 max-w-2xl text-white/80 px-2 md:px-0" style={{ lineHeight: "1.8" }}>
          Precision-sourced European Engine Components, Suspension &amp; Collision Parts.
          FCL &amp; LCL fulfillment via <strong className="text-white font-semibold">Guangzhou Port and Ningbo Port</strong>.
          Backed by real domestic distribution data — not marketing claims.
        </p>

        {/* Data Badges */}
        <div className="grid grid-cols-2 md:flex md:flex-wrap items-center justify-center gap-2 md:gap-3 mb-10 md:mb-12 w-full">
          {dataBadges.map((badge) => (
            <div
              key={badge.value}
              className="flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2.5 md:py-3 rounded-xl"
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.22)",
                backdropFilter: "blur(10px)",
              }}
            >
              <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center shrink-0">
                <i className={`${badge.icon} text-sm md:text-base`} style={{ color: "#F59E0B" }} />
              </div>
              <div className="text-left">
                <p className="text-xs md:text-sm font-bold text-white leading-none mb-0.5">{badge.value}</p>
                <p className="font-light text-white/55 hidden sm:block" style={{ fontSize: "10px" }}>{badge.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search Bar */}
        <div className="w-full max-w-3xl mb-5">
          <div
            className="relative flex items-center rounded-2xl overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.95)",
              border: "2px solid rgba(245,158,11,0.5)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div className="flex items-center justify-center w-10 md:w-14 h-full pl-3 md:pl-5 shrink-0">
              <i className="ri-search-line text-lg md:text-xl" style={{ color: "#D97706" }} />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search OE Part Number..."
              className="flex-1 bg-transparent py-4 md:py-5 px-2 md:px-3 outline-none font-light text-sm md:text-base"
              style={{ color: "#1a2332" }}
            />
            <button
              onClick={handleSearch}
              className="shrink-0 m-2 md:m-2.5 px-4 md:px-8 py-2.5 md:py-3 rounded-xl font-bold text-xs tracking-widest whitespace-nowrap transition-all duration-200 hover:brightness-110 active:scale-95 cursor-pointer text-white"
              style={{
                background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                letterSpacing: "0.14em",
              }}
            >
              SEARCH
            </button>
          </div>
          <p className="text-xs mt-3 text-center text-white/40 px-2">
            OE database integration in progress — submit part numbers via the inquiry form below for immediate response
          </p>
        </div>

        {/* Brand Logo Row */}
        <BrandLogoRow />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
        <span className="text-xs tracking-widest uppercase text-white/60" style={{ fontSize: "9px", letterSpacing: "0.2em" }}>Scroll</span>
        <div className="w-px h-10" style={{ background: "linear-gradient(to bottom, #F59E0B, transparent)" }} />
      </div>
    </section>
  );
}
